(() => {
  'use strict';

  /**
   * Shared controller for native ET carousels.
   *
   * Sections may appear more than once on a page and may be replaced by the
   * Shopify Theme Editor. Keep initialisation idempotent and release every
   * listener/timer on section unload so merchants do not accumulate handlers.
   */
  if (window.ETNativeCarousel) return;

  const controllers = new Set();
  const byRegion = new WeakMap();
  const reduceMotion = window.matchMedia('(prefers-reduced-motion: reduce)');

  function createController(region) {
    if (!region || byRegion.has(region)) return byRegion.get(region);

    const carousel = region.querySelector('.native-carousel-scroll');
    if (!carousel) return null;

    const previousButton = region.querySelector('.native-scroll-prev');
    const nextButton = region.querySelector('.native-scroll-next');
    const autoScrollSeconds = Number.parseInt(carousel.dataset.autoScroll, 10) || 0;
    let autoScrollTimer = null;

    if (previousButton) previousButton.setAttribute('aria-controls', carousel.id);
    if (nextButton) nextButton.setAttribute('aria-controls', carousel.id);

    function cardWidth() {
      const slide = carousel.querySelector('.native-carousel-slide');
      if (!slide) return 240;
      const gap = Number.parseFloat(window.getComputedStyle(carousel).columnGap) || 0;
      return slide.getBoundingClientRect().width + gap;
    }

    function maximumScroll() {
      return Math.max(0, carousel.scrollWidth - carousel.clientWidth);
    }

    function updateButtons() {
      const max = maximumScroll();
      const atStart = carousel.scrollLeft <= 1;
      const atEnd = carousel.scrollLeft >= max - 1;

      // Native disabled state prevents redundant clicks; aria-disabled keeps
      // the boundary state explicit for assistive technology.
      if (previousButton) {
        previousButton.disabled = atStart;
        previousButton.setAttribute('aria-disabled', String(atStart));
      }
      if (nextButton) {
        nextButton.disabled = atEnd;
        nextButton.setAttribute('aria-disabled', String(atEnd));
      }
    }

    function scrollByCard(direction) {
      carousel.scrollBy({
        left: cardWidth() * direction,
        behavior: reduceMotion.matches ? 'auto' : 'smooth',
      });
    }

    function onPreviousClick() {
      // Manual navigation takes ownership from autoplay to avoid a second
      // advance when an interval fires during the same interaction.
      stopAutoScroll();
      scrollByCard(-1);
    }

    function onNextClick() {
      stopAutoScroll();
      scrollByCard(1);
    }

    function onKeydown(event) {
      if (event.key === 'ArrowRight' && nextButton && !nextButton.disabled) {
        event.preventDefault();
        nextButton.click();
      }
      if (event.key === 'ArrowLeft' && previousButton && !previousButton.disabled) {
        event.preventDefault();
        previousButton.click();
      }
    }

    function stopAutoScroll() {
      if (autoScrollTimer) window.clearInterval(autoScrollTimer);
      autoScrollTimer = null;
    }

    function startAutoScroll() {
      stopAutoScroll();

      // Autoplay is optional, pauses in background tabs and never overrides a
      // shopper's reduced-motion preference.
      if (autoScrollSeconds <= 0 || reduceMotion.matches || document.hidden) return;

      autoScrollTimer = window.setInterval(() => {
        if (region.matches(':hover') || region.matches(':focus-within')) return;

        if (carousel.scrollLeft >= maximumScroll() - 1) {
          carousel.scrollTo({ left: 0, behavior: 'smooth' });
        } else {
          scrollByCard(1);
        }
      }, autoScrollSeconds * 1000);
    }

    function onVisibilityChange() {
      if (document.hidden) stopAutoScroll();
      else startAutoScroll();
    }

    function onMotionPreferenceChange() {
      if (reduceMotion.matches) stopAutoScroll();
      else startAutoScroll();
    }

    if (previousButton) previousButton.addEventListener('click', onPreviousClick);
    if (nextButton) nextButton.addEventListener('click', onNextClick);
    carousel.addEventListener('keydown', onKeydown);
    carousel.addEventListener('scroll', updateButtons, { passive: true });
    region.addEventListener('mouseenter', stopAutoScroll);
    region.addEventListener('mouseleave', startAutoScroll);
    region.addEventListener('focusin', stopAutoScroll);
    region.addEventListener('focusout', startAutoScroll);
    region.addEventListener('pointerdown', stopAutoScroll);
    window.addEventListener('pointerup', startAutoScroll);
    window.addEventListener('resize', updateButtons, { passive: true });
    document.addEventListener('visibilitychange', onVisibilityChange);
    reduceMotion.addEventListener('change', onMotionPreferenceChange);

    updateButtons();
    startAutoScroll();

    const controller = {
      region,
      destroy() {
        stopAutoScroll();
        if (previousButton) previousButton.removeEventListener('click', onPreviousClick);
        if (nextButton) nextButton.removeEventListener('click', onNextClick);
        carousel.removeEventListener('keydown', onKeydown);
        carousel.removeEventListener('scroll', updateButtons);
        region.removeEventListener('mouseenter', stopAutoScroll);
        region.removeEventListener('mouseleave', startAutoScroll);
        region.removeEventListener('focusin', stopAutoScroll);
        region.removeEventListener('focusout', startAutoScroll);
        region.removeEventListener('pointerdown', stopAutoScroll);
        window.removeEventListener('pointerup', startAutoScroll);
        window.removeEventListener('resize', updateButtons);
        document.removeEventListener('visibilitychange', onVisibilityChange);
        reduceMotion.removeEventListener('change', onMotionPreferenceChange);
        controllers.delete(controller);
        byRegion.delete(region);
      },
    };

    byRegion.set(region, controller);
    controllers.add(controller);
    return controller;
  }

  function regionsWithin(root) {
    const regions = [];
    if (root.matches && root.matches('.et-nt-carousel-region')) regions.push(root);
    if (root.querySelectorAll) regions.push(...root.querySelectorAll('.et-nt-carousel-region'));
    return regions;
  }

  function init(root = document) {
    regionsWithin(root).forEach(createController);
  }

  function destroy(root) {
    Array.from(controllers).forEach((controller) => {
      if (controller.region === root || root.contains(controller.region)) controller.destroy();
    });
  }

  window.ETNativeCarousel = { init, destroy };

  // The public storefront uses the initial document lifecycle. Theme Editor
  // events below cover sections that Shopify injects or removes afterwards.
  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', () => init(), { once: true });
  } else {
    init();
  }

  document.addEventListener('shopify:section:load', (event) => init(event.target));
  document.addEventListener('shopify:section:unload', (event) => destroy(event.target));
})();
